/* The range viewer: pick a spot in the tree on the left, read its chart on the right. In edit
   mode the same page writes charts — over the collection's own, or for spots it never held —
   into a collection of the user's own. */

import "../../styles/base.css";
import "./ranges.css";

import { actionLabel, autoSeats, resolveTree, type TreeState, seatName } from "@preflop/core";
import { wireDaily } from "../../state/daily";
import { countDeviceEdits } from "../../state/edits";
import { loadEquity } from "../../state/equity";
import { showAccount, describeSession, openSession, wireSwitcher } from "../../state/session";
import { loadSetting, saveSetting } from "../../state/settings";
import { showFatal, showLoading } from "../../ui/boot";
import { byId, el, fill } from "../../ui/dom";
import { topbar } from "../../ui/topbar";
import { canPaint, clearNoteInputs, paintHand } from "./brush";
import { cancelPreview, paintChart } from "./chart";
import { renderControls } from "./controls";
import { copyTo, renderCopyTo } from "./copy-to";
import { duplicateCurrent, openCreator, renderCreator } from "./creator";
import { renderEditsPanel } from "./edits-panel";
import { dom, grid, tooltip } from "./layout";
import { renderNotation } from "./notation-panel";
import { RANDOMIZER_SETTING, initRandomizer, renderRandom } from "./randomizer";
import { NEW_COPY, OPEN_SPOT, SAVE_TARGET_SETTING } from "./save";
import { confirmDiscard, jumpTo, onRender, openPage, page } from "./state";

/* ---- the write tools, at the foot of the picker ---- */

const newSpotBtn = el(
  "button",
  {
    type: "button",
    class: "copy",
    title: "Write a chart for a spot the collection does not have",
    hidden: true,
    onclick: () => openCreator(),
  },
  "+ New spot",
);
const dupBtn = el(
  "button",
  {
    type: "button",
    class: "copy",
    title: "Open the creator on a copy of this chart",
    hidden: true,
    onclick: duplicateCurrent,
  },
  "Duplicate",
);
const editModeBtn = el(
  "button",
  {
    type: "button",
    class: "copy",
    title: "Paint the grid and write new charts",
    ariaPressed: "false",
    disabled: true, // until there is a page to edit
    dataset: { testid: "edit-mode" },
    onclick: toggleEditMode,
  },
  "Edit mode",
);
const bar = topbar("ranges");
// Edit mode last: it is the switch the other three answer to, and the one the hand returns to.
dom.tools.append(copyTo, newSpotBtn, dupBtn, editModeBtn);

function toggleEditMode(): void {
  const p = page();
  if (p.editMode && !confirmDiscard()) return;
  p.editMode = !p.editMode;
  void saveSetting("editMode", p.editMode);
  if (!p.editMode) p.creator = null;
  render();
}

function renderTools(): void {
  const p = page();
  editModeBtn.textContent = p.editMode ? "Editing" : "Edit mode";
  editModeBtn.ariaPressed = p.editMode ? "true" : "false";
  newSpotBtn.hidden = !p.editMode;
  // Nothing on screen to copy when nothing is charted.
  dupBtn.hidden = !p.editMode || !p.spot;
  renderCopyTo();
  bar.setState(describeSession(p.session));
}

/* ---- the whole page ---- */

/* No chart anywhere. The tree has nothing to draw and the chart panel nothing to fill, so
   the page is only the write tools and the way in to the first spot — without this the whole
   screen would come up blank. */
function renderEmpty(): void {
  const p = page();
  renderTools();
  renderEditsPanel();
  clearNoteInputs();
  renderCreator();

  fill(dom.picker);
  dom.picker.hidden = true;
  // An empty grid and an empty notation panel read as a chart that failed to load. There is
  // no chart, so neither is drawn at all.
  for (const node of [grid.root, dom.legend, dom.bar, dom.notation]) node.hidden = true;
  dom.approx.textContent = "";

  const offer = dom.missing;
  fill(offer);
  offer.hidden = !!p.creator;
  if (offer.hidden) return;
  offer.append("No charts yet.");
  if (!p.editMode) {
    offer.append(" Turn on edit mode to write the first one.");
    return;
  }
  offer.append(
    " ",
    el(
      "button",
      {
        type: "button",
        class: "copy",
        title: "Open the creator on an empty chart",
        onclick: () => openCreator(),
      },
      "Create the first spot",
    ),
  );
}

/* The tree snapped somewhere else because nothing was charted there. Reading, that is
   invisible on purpose; editing, it is exactly the spot to write. */
function renderMissing(missing: TreeState | null): void {
  const p = page();
  const offer = dom.missing;
  fill(offer);
  // Opponent seats carry over from the action that was on screen, so they are fitted to the
  // one being asked for before anything is promised. Null means no table can deal this at
  // all — BB can never be first in — and then there is no gap to fill: the offer is withdrawn
  // rather than reworded.
  const asks =
    missing?.gameType && missing.hero && missing.action
      ? autoSeats(missing.gameType, missing.hero, missing.action, missing.seats)
      : null;
  offer.hidden = !(p.editMode && missing && !p.creator && asks);
  if (offer.hidden || !missing || !asks) return;
  const { stack, openSize, hero, action } = missing;
  const label = [
    stack,
    openSize,
    hero && seatName(hero),
    action ? actionLabel(action) : null,
    ...asks.map(seatName),
  ]
    .filter(Boolean)
    .join(" · ");
  offer.append(
    `Nothing charted for ${label}.`,
    " ",
    el(
      "button",
      {
        type: "button",
        class: "copy",
        title: "Open the creator on these filters",
        onclick: () =>
          openCreator({
            ...(stack ? { stack } : {}),
            ...(openSize ? { openSize } : {}),
            ...(hero ? { hero } : {}),
            ...(action ? { action } : {}),
            seats: [...asks],
          }),
      },
      "Create it",
    ),
  );
}

function render(): void {
  const p = page();
  p.adding = null; // navigation closes an open "+" field
  // Snapped onto a combination that exists; what was asked for is kept beside it.
  const { state, spot, missing } = resolveTree(p.spots, p.tree);
  p.tree = state;
  p.spot = spot;
  if (!spot) return renderEmpty();
  // Undo what the empty screen hid, for the render after the first spot lands.
  for (const node of [grid.root, dom.legend, dom.bar, dom.notation]) node.hidden = false;

  renderMissing(missing);
  renderTools();
  renderRandom(spot);
  renderControls(spot);
  // Both panels below re-register the brush target if they put fields up. Left over from the
  // last render, it would point at textareas nobody can see.
  clearNoteInputs();
  renderCreator();
  renderNotation(spot); // settles the draft the chart is drawn from
  cancelPreview();
  paintChart(spot);
  renderEditsPanel();
}

/* ---- boot ---- */

async function boot(): Promise<void> {
  const host = el("main");
  byId("app").append(bar.root, host);
  showLoading(host);
  try {
    const session = await openSession();
    showAccount(bar, session);
    wireDaily(bar);
    const [waiting, editMode, adds, randomizer, target] = await Promise.all([
      countDeviceEdits().catch(() => 0),
      loadSetting("editMode", false),
      loadSetting<unknown>("catalogAdds", null),
      loadSetting<unknown>(RANDOMIZER_SETTING, false),
      loadSetting<unknown>(SAVE_TARGET_SETTING, NEW_COPY),
    ]);
    // Settled before the first chart is drawn, so a reload does not flash the other view.
    // Off unless it was switched on: anything else in the slot is not a choice.
    initRandomizer(randomizer === true);
    const p = openPage(session, {
      editMode: editMode === true,
      adds,
      target: typeof target === "string" ? target : NEW_COPY,
      waiting,
    });
    // A save out of a read-only collection reloads into the collection the chart went to,
    // and lands on the chart that was saved rather than on the first one of the tree.
    const landOn = sessionStorage.getItem(OPEN_SPOT);
    sessionStorage.removeItem(OPEN_SPOT);
    const saved = landOn ? p.index.get(landOn) : undefined;
    if (saved) jumpTo(saved);
    // Typed and unsaved notation is asked about before a switch takes the page away.
    wireSwitcher(bar, session, confirmDiscard);

    onRender(render);
    // A stroke answers at once, unlike a keystroke.
    grid.bindPaint({
      enabled: canPaint,
      stroke: (hand, only) => {
        if (!paintHand(hand, only)) return;
        cancelPreview();
        paintChart();
      },
    });
    fill(host, dom.page, tooltip.root);
    editModeBtn.disabled = false;
    render();

    // 43 KB that only the tooltip reads: the chart does not wait for it, and is drawn again
    // with its equity lines when the table lands.
    void loadEquity().then((table) => {
      p.equity = table;
      p.equityLoaded = true;
      paintChart();
    });
  } catch (e) {
    showFatal(host, e);
  }
}

void boot();
